import { inject } from '@angular/core';
import { PostItem } from '@app/core/models';
import { Store } from '@ngrx/store';
import { ItemFn } from '../item-post/item-post.component';

import { Dialog } from '@angular/cdk/dialog';
import { ACADEMIC_YEAR_KEY } from '@app/config';
import { EclassWorkerEnum } from '@app/core/models/eclass-worker.enum';
import { AnnouncementService } from '@app/core/services/announcement.service';
import { ModalAnnouncementReadComponent } from '@app/modules/eclass/components/modal-announcement-read/modal-announcement-read.component';
import { Checker, helperAcademicYear, notNull, safeEmpty } from '@app/shared/utils';
import { AppSelectors } from '@app/store';
import { switchMap, take, map, tap, catchError } from 'rxjs/operators';
import { SubtitleAnnouncementComponent } from './subtitle-announcement.component';
import { Header } from '../../utils/postable';
import { ModalAnnouncementNewComponent } from '@app/modules/eclass/components/modal-announcement-new/modal-announcement-new.component';
import { FilterService } from '../../services/filter.service';
import { of } from 'rxjs';
import { AlertService, NotificationService } from '@app/core';
import { AnnouncementTypeEnum } from './post-announcement.component';
import { AppState } from '@app/store/app.state';
import { FilterControlEnum, FilterForm, StatusFilter, StatusFilterTypeEnum } from '../posts-filter-bar/filter-bar';

interface Service {
  store: Store;
  dialog: Dialog;
  alertService: AlertService;
  filterService: FilterService;
  announcementService: AnnouncementService;
  notificationService: NotificationService;
}

export const AnnouncementFilter = (state: AppState) => ({
  status: [
    {
      key: StatusFilterTypeEnum.IsConcluded,
      type: StatusFilterTypeEnum.IsConcluded,
      value: undefined,
      description: 'geral.todos',
    },
    {
      key: `${StatusFilterTypeEnum.HasAttachments}#1`,
      type: StatusFilterTypeEnum.HasAttachments,
      value: 1,
      description: 'eclass.relatorios.com-anexos',
    },
  ],
  filters: filters(state),
});

export const AnnouncementHeader: Header = {
  plural: 'eclass.agenda.filtros.comunicado.plural',
  singular: 'eclass.agenda.filtros.comunicado.singular',
  icon: 'icon-announcement',
  worker: EclassWorkerEnum.Announcement,
};

export const AnnouncementItem: ItemFn = () => {
  const services: Service = {
    store: inject(Store),
    dialog: inject(Dialog),
    alertService: inject(AlertService),
    filterService: inject(FilterService),
    announcementService: inject(AnnouncementService),
    notificationService: inject(NotificationService),
  };

  return {
    ...AnnouncementHeader,
    color: 'border-modules-school-500 outline-modules-school-500',
    iconStyle: 'text-modules-school-700 bg-modules-school-500/10',
    labelStyle: 'border-modules-school-500 text-modules-school-700',
    subtitle: SubtitleAnnouncementComponent,
    action: (item) => readOnly(item, services),
    options: (item) => options(item, services),
  };
};

function readOnly(item: PostItem, service: Service) {
  const activeUser$ = service.store.select(AppSelectors.ActiveUser);
  return activeUser$.pipe(
    notNull(),
    take(1),
    switchMap((activeUser) =>
      service.announcementService
        .show(item.reference_id, {}, { [ACADEMIC_YEAR_KEY.year]: helperAcademicYear(item.date, activeUser) })
        .pipe(safeEmpty()),
    ),

    switchMap((data) => service.dialog.open(ModalAnnouncementReadComponent, { data, disableClose: true }).closed),
  );
}

function options(item: PostItem, service: Service) {
  return service.store.select(AppSelectors.isAllow([20, 41])).pipe(
    notNull(),
    take(1),
    map(() => {
      if (item.filter === AnnouncementTypeEnum.Sent) {
        return [
          {
            icon: 'icon-edit',
            title: 'geral.editar',
            action: () => edit(item, service),
          },
          {
            icon: 'icon-trash-2',
            title: 'geral.excluir',
            action: () => remove(item, service),
          },
        ];
      }
      return [];
    }),
  );
}

function edit(item: PostItem, service: Service) {
  return service.store.select(AppSelectors.ActiveUser).pipe(
    notNull(),
    take(1),
    switchMap((activeUser) =>
      service.announcementService
        .show(item.reference_id, {}, { [ACADEMIC_YEAR_KEY.year]: helperAcademicYear(item.date, activeUser) })
        .pipe(safeEmpty()),
    ),
    switchMap(
      (announcement) =>
        service.dialog.open(ModalAnnouncementNewComponent, { data: { announcement }, disableClose: true }).closed,
    ),
    tap((res) => {
      if (res) {
        service.filterService.refresh();
      }
    }),
  );
}

function remove(item: PostItem, service: Service) {
  return service.alertService.confirm({ message: 'comunicados.excluir' }).pipe(
    notNull(),
    switchMap(() =>
      service.announcementService.deleteAnnoucement(item.reference_id).pipe(catchError(() => of({ ret: 0 }))),
    ),
    tap((res) => {
      if (res.ret === 1) {
        service.notificationService.success('comunicados.comunicado-excluido');
        service.filterService.refresh();
      } else {
        service.notificationService.error('comunicados.erro-excluir');
      }
    }),
  );
}

function filters(store: AppState): FilterForm {
  const filters = [FilterControlEnum.Generic];
  if (Checker.isEmployee(store.funcao_padrao)) {
    filters.push(FilterControlEnum.UserGroup);
  }
  return {
    fixed: filters,
    filters,
  };
}
